import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { buildLeadFilterUrl } from '@/shared/lib/leadNavigation'

import SalespersonTablesSection from './SalespersonTablesSection'

export default function TargetAchievementCard({
  target = 0,
  achievement = 0,
  achievementRevenue = 0,
  salespersonData = [],
  isTeam = true,
  period,
  customRange,
}) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const navigate = useNavigate()
  const targetVal = Number(target) || 0
  const achievementVal = Number(achievement) || 0
  const revenueVal = Number(achievementRevenue) || 0
  const percentage =
    targetVal > 0 ? Math.round((revenueVal / targetVal) * 100) : 0
  const remainingRevenue = Math.max(0, targetVal - revenueVal)

  // Use achievementRevenue if provided, otherwise fallback to achievementVal
  // Format as Indian Rupee
  const displayRevenue = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(revenueVal)

  const displayTarget = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(targetVal)

  const displayRemaining = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(remainingRevenue)

  const handleCardClick = () => {
    setIsModalOpen(true)
  }

  // Calculate combined total revenue for all salespersons
  const totalCombinedRevenue = salespersonData.reduce(
    (sum, p) => sum + (Number(p.revenue) || 0),
    0,
  )
  const displayTotalCombinedRevenue = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(totalCombinedRevenue)

  return (
    <>
      <Card
        onClick={handleCardClick}
        className="h-[260px] cursor-pointer overflow-hidden rounded-2xl border border-black/[0.06] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.04)] transition hover:border-slate-300 hover:shadow-md sm:rounded-[20px]"
      >
        <div className="flex h-full flex-col p-4">
          {/* Header without icon */}
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-slate-900">
              {isTeam ? 'Team Target' : 'Target & Achievement'}
            </h3>
            <span className="text-[10px] font-semibold text-emerald-600 hover:underline">
              View Won Leads &rarr;
            </span>
          </div>

          {/* Big Numbers Comparison */}
          <div className="mt-2.5 grid grid-cols-2 gap-2 rounded-xl border border-black/[0.04] bg-slate-50/80 p-2.5">
            <div>
              <p className="text-[10px] font-medium text-slate-500">
                Total Achieved (Won)
              </p>
              <div className="flex flex-col mt-0.5">
                <p
                  className="text-sm sm:text-base lg:text-lg font-extrabold leading-tight tracking-tighter text-emerald-600"
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {displayRevenue}
                </p>
                <p className="text-[11px] font-semibold text-emerald-700/80 mt-1">
                  {achievementVal} {achievementVal === 1 ? 'unit' : 'units'}
                </p>
              </div>
            </div>
            <div className="border-l border-slate-200 pl-2.5">
              <p className="text-[10px] font-medium text-slate-500">
                Team Target
              </p>
              <div className="flex flex-col mt-0.5">
                <p
                  className="text-sm sm:text-base lg:text-lg font-extrabold leading-tight tracking-tighter text-slate-900"
                  style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                >
                  {displayTarget}
                </p>
                <p className="text-[11px] font-semibold text-slate-500 mt-1">
                  total value by all sales
                </p>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-2.5 space-y-1">
            <div className="flex items-center justify-between text-[10px]">
              <span className="font-medium text-slate-500">Team Progress</span>
              <span className="font-bold text-slate-800">{percentage}%</span>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all duration-500"
                style={{ width: `${Math.min(100, percentage)}%` }}
              />
            </div>
          </div>

          {/* Breakdown details */}
          <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-2 text-[11px]">
            <span className="text-slate-500">Remaining to Target</span>
            <span className="font-bold text-slate-900">{displayRemaining}</span>
          </div>
        </div>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden sm:rounded-2xl max-h-[90vh] flex flex-col">
          <div className="border-b border-slate-100 bg-slate-50/50 p-6 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-lg font-bold text-slate-900">
                  Salesperson Target & Leads Breakdown
                </DialogTitle>
                <p className="mt-1 text-sm font-semibold text-emerald-600">
                  Total Combined Revenue: {displayTotalCombinedRevenue}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsModalOpen(false)
                  navigate(
                    buildLeadFilterUrl('/admin/leads', {
                      status: 'won',
                      period,
                      customRange,
                    }),
                  )
                }}
                className="gap-1.5 rounded-full text-xs font-semibold"
              >
                View Won Leads Table
              </Button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Breakdown of assigned leads and performance for each salesperson.
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            {salespersonData.length === 0 ? (
              <p className="text-center text-sm text-slate-500 py-8">
                No salesperson data available.
              </p>
            ) : (
              <SalespersonTablesSection
                data={salespersonData}
                period={period}
                customRange={customRange}
              />
            )}
          </div>

          <div className="flex items-center justify-end border-t border-slate-100 bg-white px-6 py-4 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsModalOpen(false)}
              className="rounded-full"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
